import { useState, useEffect } from 'react';
import UserAPI from 'services/API/UserAPI';
import { useSelectedUserAtom } from 'state/auth';

export interface User {
  id: number;
  first_name: string;
  email: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  timezone: string;
  last_login: string;
  job_title: string;
}

interface CreateUserPayload {
  first_name: string;
  last_name: string;
  email: string;
  job_title: string;
}

interface ChangePasswordPayload {
  current_password: string;
  new_password_1: string;
  new_password_2: string;
}

const useUserAccount = () => {
  const [selectedUser, setSelectedUser] = useSelectedUserAtom();
  const [key, setKey] = useState(0);

  const fetchUser = async (userId: number) => {
    const response = await UserAPI.getUser(userId);
    setSelectedUser(response.data.data);
  };

  const editUser = async (
    userId: number,
    data: Pick<User, 'first_name' | 'last_name' | 'timezone' | 'job_title'>,
  ) => {
    await UserAPI.patchUser(userId, data);
    setKey(key + 1);
  };

  const editUserPassword = async (data: ChangePasswordPayload) => {
    const response = await UserAPI.postUpdatePassword(data);

    return {
      status: response?.status || 500,
    };
  };

  const createUser = async (data: CreateUserPayload) => {
    return UserAPI.postCreateUser(data)
      .then((response) => {
        return {
          status: response?.status || 500,
          message: response?.data?.message,
        };
      })
      .catch((error) => {
        if (error.response) {
          return {
            status: error.response.status || 500,
            message: error.response.data?.message,
          };
        }
        throw error;
      });
  };

  useEffect(() => {
    if (key && selectedUser?.id) {
      fetchUser(selectedUser.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, selectedUser?.id]);

  return {
    selectedUser,
    fetchUser,
    editUser,
    createUser,
    editUserPassword,
  };
};

export default useUserAccount;
